import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to track online/offline status and connection information
 * @returns {Object} Object containing network status and connection info
 * @property {boolean} isOnline - Whether the device is currently online
 * @property {boolean} wasOffline - Whether the device was offline and came back online
 * @property {string} connectionType - Type of network connection ('wifi', 'cellular', 'ethernet', 'none')
 * @property {boolean} isSlowConnection - Whether the connection is slow (effectiveType is 'slow-2g', '2g', or '3g')
 * @property {number} downlink - Effective bandwidth estimate in megabits per second
 * @property {number} rtt - Estimated effective round-trip time in milliseconds
 * @property {boolean} saveData - Whether the user has requested reduced data usage
 * @property {number} offlineAt - Timestamp when the device went offline (0 if online)
 * @property {number} onlineAt - Timestamp when the device came back online (0 if offline)
 * @property {number} offlineDuration - How long the device was offline in milliseconds (0 if online)
 */
export function useNetworkStatus() {
  // Track the previous online state to detect changes
  const prevOnlineRef = useRef(navigator.onLine);
  // Track when we went offline
  const offlineAtRef = useRef(0);
  // Track when we came back online
  const onlineAtRef = useRef(0);
  // Track offline duration
  const [offlineDuration, setOfflineDuration] = useState(0);
  // Track if we should show the offline UI (with a small delay to avoid flickering)
  const [showOfflineUI, setShowOfflineUI] = useState(false);
  // Track if we should show the back online UI (temporarily after coming back online)
  const [showBackOnlineUI, setShowBackOnlineUI] = useState(false);
  
  // Main online/offline state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  
  // Connection information
  const [connection, setConnection] = useState({
    effectiveType: '',
    type: '',
    downlink: 0,
    rtt: 0,
    saveData: false,
  });

  // Handle connection change
  const updateOnlineStatus = useCallback(() => {
    const now = Date.now();
    const online = navigator.onLine;
    const wasOnline = prevOnlineRef.current;
    
    // Only update if the state actually changed
    if (online !== wasOnline) {
      prevOnlineRef.current = online;
      setIsOnline(online);
      
      if (online) {
        // We just came back online
        onlineAtRef.current = now;
        const wasOfflineFor = offlineAtRef.current > 0 ? now - offlineAtRef.current : 0;
        setOfflineDuration(wasOfflineFor);
        setWasOffline(true);
        setShowOfflineUI(false);
        setShowBackOnlineUI(true);
        
        // Hide the back online message after 5 seconds
        const timer = setTimeout(() => {
          setWasOffline(false);
          setShowBackOnlineUI(false);
        }, 5000);
        
        return () => clearTimeout(timer);
      } else {
        // We just went offline
        offlineAtRef.current = now;
        setShowOfflineUI(true);
        setShowBackOnlineUI(false);
      }
    }
    
    return undefined;
  }, []);

  // Debounce connection updates to avoid too many re-renders
  const debounceTimer = useRef(null);
  
  // Handle network information changes with debouncing
  const updateConnectionInfo = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      // Use type checking instead of TypeScript assertion
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (conn) {
        setConnection({
          effectiveType: conn.effectiveType || '',
          type: conn.type || '',
          downlink: conn.downlink || 0,
          rtt: conn.rtt || 0,
          saveData: conn.saveData || false,
        });
      }
      
      debounceTimer.current = null;
    }, 100); // 100ms debounce
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Set up event listeners
  useEffect(() => {
    // Add event listeners for online/offline status
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    
    // Add event listener for connection changes
    if ('connection' in navigator) {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn) {
        conn.addEventListener('change', updateConnectionInfo);
        // Initial connection info update
        updateConnectionInfo();
      }
    }
    
    // Initial update
    updateOnlineStatus();
    
    // Set up a periodic check for connection changes (as a fallback)
    const connectionCheckInterval = setInterval(updateConnectionInfo, 30000);
    
    // Clean up function
    return () => {
      // Remove event listeners
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      
      // Clear the interval
      clearInterval(connectionCheckInterval);
      
      // Clean up connection listeners
      if ('connection' in navigator) {
        const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (conn) {
          conn.removeEventListener('change', updateConnectionInfo);
        }
      }
      
      // Clear any pending debounce timers
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [updateOnlineStatus, updateConnectionInfo]);

  // Determine if the connection is slow
  const isSlowConnection = connection.effectiveType 
    ? ['slow-2g', '2g', '3g'].includes(connection.effectiveType)
    : false;

  return {
    // Connection status
    isOnline,
    wasOffline,
    showOfflineUI,
    showBackOnlineUI,
    
    // Connection type and quality
    connectionType: connection.type,
    isSlowConnection,
    effectiveType: connection.effectiveType,
    
    // Connection metrics
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
    
    // Timestamps
    offlineAt: offlineAtRef.current,
    onlineAt: onlineAtRef.current,
    offlineDuration,
    
    // Raw connection info
    connectionInfo: connection,
  };
}

/**
 * Higher-order component that provides network status props to the wrapped component
 * @param {React.ComponentType} WrappedComponent - The component to wrap
 * @returns {React.ComponentType} - Wrapped component with network status props
 */
export function withNetworkStatus(WrappedComponent) {
  return function WithNetworkStatus(props) {
    const networkStatus = useNetworkStatus();
    return <WrappedComponent {...props} networkStatus={networkStatus} />;
  };
}
